import React from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://businessdevelopmentauthority.net';

const DigitalTransformationPage: React.FC = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/digital-transformation`,
        'url': `${SITE_URL}/digital-transformation`,
        'name': 'Digital Transformation Services - Business Development Authority',
        'description': 'Reimagine your business for the digital age with comprehensive digital transformation strategies — cloud migration, data modernization, process digitization, and workforce enablement.',
        'isPartOf': { '@id': SITE_URL },
      },
      {
        '@type': 'Service',
        'name': 'Digital Transformation',
        'url': `${SITE_URL}/digital-transformation`,
        'description': 'End-to-end digital transformation programs covering cloud transformation, data modernization, customer experience redesign, and cultural change management.',
        'serviceType': 'Digital Transformation Consulting',
        'provider': {
          '@type': 'Organization',
          'name': 'Business Development Authority',
          'url': SITE_URL,
        },
        'areaServed': 'US',
        'hasOfferCatalog': {
          '@type': 'OfferCatalog',
          'name': 'Digital Transformation Services',
          'itemListElement': [
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Cloud Transformation' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Data Modernization' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Customer Experience Redesign' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Process Digitization' } },
            { '@type': 'Offer', 'itemOffered': { '@type': 'Service', 'name': 'Workforce Enablement' } },
          ],
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="min-h-screen">
        <Header />
        
        {/* Hero Section */}
        <section className="relative min-h-[600px] flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/81e2a5c32b7d724c497299741e487af9.jpg')` }}>
          <div className="max-w-7xl mx-auto px-6 text-center text-white w-full">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Digital Transformation</h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">Reimagine your business for the digital age with comprehensive transformation strategies and cutting-edge technology solutions</p>
            <button onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })} className="whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer">
              Start Your Transformation
            </button>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">Evolve Your Business for the Digital Future</h2>
                <p className="text-lg text-gray-700 mb-6">
                  Digital Transformation is more than technology adoption—it's a fundamental reimagining of how your business operates, delivers value, and competes in the digital economy. We guide organizations through every stage of their transformation journey.
                </p>
                <p className="text-lg text-gray-700 mb-6">
                  From legacy system modernization to cloud migration, from process digitization to cultural change, we deliver comprehensive transformation programs that drive sustainable competitive advantage.
                </p>
              </div>
              <div className="relative h-[400px] rounded-lg overflow-hidden shadow-2xl">
                <img src="https://readdy.ai/api/search-image?query=Business%20team%20collaborating%20on%20digital%20transformation%20strategy%20with%20interactive%20touchscreens%20and%20cloud%20technology%20displays%2C%20modern%20office%20with%20digital%20innovation%20tools%2C%20professional%20corporate%20setting&width=600&height=400&seq=digital-transform-overview&orientation=landscape" alt="Digital Transformation" className="w-full h-full object-cover object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* Transformation Pillars Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12">Transformation Pillars</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-cloud-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Cloud Transformation</h3>
                <p className="text-gray-700">Migrate to cloud infrastructure for scalability, flexibility, and cost optimization with modern cloud-native architectures.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-database-2-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Data Modernization</h3>
                <p className="text-gray-700">Transform data infrastructure and implement advanced analytics for data-driven decision making.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-customer-service-2-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Customer Experience</h3>
                <p className="text-gray-700">Redesign customer journeys with digital-first experiences that drive engagement and loyalty.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-settings-3-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Process Digitization</h3>
                <p className="text-gray-700">Digitize and optimize business processes for improved efficiency and operational excellence.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-team-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Workforce Enablement</h3>
                <p className="text-gray-700">Empower employees with digital tools, skills, and collaborative technologies for the modern workplace.</p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-lg mb-6">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4">Security & Compliance</h3>
                <p className="text-gray-700">Implement robust cybersecurity frameworks and ensure compliance in the digital environment.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Approach Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12">Our Transformation Approach</h2>
            <div className="space-y-8">
              <div className="flex items-start space-x-6">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-compass-3-line text-3xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">Vision & Strategy</h3>
                  <p className="text-gray-700 text-lg">Define your digital vision, assess current state, and create a comprehensive transformation roadmap aligned with business objectives.</p>
                </div>
              </div>
              <div className="flex items-start space-x-6">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-tools-line text-3xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">Technology Implementation</h3>
                  <p className="text-gray-700 text-lg">Deploy modern technologies, integrate systems, and build digital capabilities that enable transformation goals.</p>
                </div>
              </div>
              <div className="flex items-start space-x-6">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-user-heart-line text-3xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">Change Management</h3>
                  <p className="text-gray-700 text-lg">Drive organizational adoption through training, communication, and cultural transformation initiatives.</p>
                </div>
              </div>
              <div className="flex items-start space-x-6">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                  <i className="ri-line-chart-line text-3xl text-blue-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">Continuous Optimization</h3>
                  <p className="text-gray-700 text-lg">Monitor performance, measure outcomes, and continuously optimize digital capabilities for sustained value delivery.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-4xl font-bold text-center mb-12">Transformation Outcomes</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <i className="ri-speed-up-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Increased Agility</h3>
                <p className="text-gray-700">Respond faster to market changes and customer needs</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Cost Reduction</h3>
                <p className="text-gray-700">Optimize operations and reduce infrastructure costs</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <i className="ri-user-smile-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Enhanced Experience</h3>
                <p className="text-gray-700">Deliver superior customer and employee experiences</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                  <i className="ri-rocket-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">Innovation Acceleration</h3>
                <p className="text-gray-700">Enable rapid innovation and new business models</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Business?</h2>
            <p className="text-xl mb-8">Let's create a digital transformation roadmap that positions your organization for long-term success.</p>
            <button onClick={() => window.REACT_APP_NAVIGATE('/calendar')} className="whitespace-nowrap bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer">
              Begin Your Journey
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default DigitalTransformationPage;
